import React, { useEffect, useRef } from "react";
import { useEditor, EditorContent, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TextStyle, Color, FontSize } from "@tiptap/extension-text-style";
import Image from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";
import { Bold, Italic, Underline, Strikethrough, Heading2, Heading3, List, ListOrdered, Quote, Link2, ImagePlus, AlignLeft, AlignCenter, AlignRight, Undo2, Redo2, Minus, Eraser } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const SIZES = [["", "Size"], ["14px", "Small"], ["16px", "Normal"], ["20px", "Large"], ["26px", "Huge"]];
const COLORS = ["#ffffff", "#a3a3a3", "#ef4444", "#f59e0b", "#22c55e", "#3b82f6", "#a855f7"];

const Btn = ({ on, active, title, children, testid }) => (
  <button type="button" title={title} onMouseDown={(e) => { e.preventDefault(); on(); }} data-testid={testid}
    className={`p-1.5 rounded-md transition-colors ${active ? "bg-white/15 text-white" : "text-white/60 hover:text-white hover:bg-white/5"}`}>{children}</button>
);

function Toolbar({ editor }) {
  const fileRef = useRef(null);
  const st = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      bold: e.isActive("bold"), italic: e.isActive("italic"), underline: e.isActive("underline"), strike: e.isActive("strike"),
      h2: e.isActive("heading", { level: 2 }), h3: e.isActive("heading", { level: 3 }), ul: e.isActive("bulletList"), ol: e.isActive("orderedList"),
      quote: e.isActive("blockquote"), link: e.isActive("link"), size: e.getAttributes("textStyle").fontSize || "",
      center: e.isActive({ textAlign: "center" }), right: e.isActive({ textAlign: "right" }),
    }),
  });
  const c = () => editor.chain().focus();
  const setLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL (leave empty to remove)", prev);
    if (url === null) return;
    if (!url) c().unsetLink().run();
    else if (/^https?:\/\//i.test(url)) c().extendMarkRange("link").setLink({ href: url }).run();
    else toast.error("Link must start with http:// or https://");
  };
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try { const r = await api.post("/admin/media", fd); c().setImage({ src: `${process.env.REACT_APP_BACKEND_URL}${r.data.url}` }).run(); }
    catch { toast.error("Image upload failed"); }
  };
  return (
    <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-white/10 bg-black/40 sticky top-0 z-10" data-testid="rte-toolbar">
      <Btn on={() => c().toggleHeading({ level: 2 }).run()} active={st.h2} title="Heading" testid="rte-h2"><Heading2 size={16} /></Btn>
      <Btn on={() => c().toggleHeading({ level: 3 }).run()} active={st.h3} title="Subheading" testid="rte-h3"><Heading3 size={16} /></Btn>
      <select value={st.size} onChange={(e) => (e.target.value ? c().setFontSize(e.target.value).run() : c().unsetFontSize().run())}
        className="mx-1 h-7 rounded-md bg-black/40 border border-white/10 text-xs text-white px-1" data-testid="rte-size">
        {SIZES.map(([v, l]) => <option key={l} value={v}>{l}</option>)}
      </select>
      <span className="w-px h-5 bg-white/10 mx-1" />
      <Btn on={() => c().toggleBold().run()} active={st.bold} title="Bold" testid="rte-bold"><Bold size={16} /></Btn>
      <Btn on={() => c().toggleItalic().run()} active={st.italic} title="Italic" testid="rte-italic"><Italic size={16} /></Btn>
      <Btn on={() => c().toggleUnderline().run()} active={st.underline} title="Underline" testid="rte-underline"><Underline size={16} /></Btn>
      <Btn on={() => c().toggleStrike().run()} active={st.strike} title="Strikethrough" testid="rte-strike"><Strikethrough size={16} /></Btn>
      <span className="flex items-center gap-1 mx-1" data-testid="rte-colors">
        {COLORS.map((col) => <button key={col} type="button" title={col} onMouseDown={(e) => { e.preventDefault(); c().setColor(col).run(); }} className="w-4 h-4 rounded-full border border-white/20" style={{ background: col }} />)}
      </span>
      <span className="w-px h-5 bg-white/10 mx-1" />
      <Btn on={() => c().toggleBulletList().run()} active={st.ul} title="Bullet list" testid="rte-ul"><List size={16} /></Btn>
      <Btn on={() => c().toggleOrderedList().run()} active={st.ol} title="Numbered list" testid="rte-ol"><ListOrdered size={16} /></Btn>
      <Btn on={() => c().toggleBlockquote().run()} active={st.quote} title="Quote" testid="rte-quote"><Quote size={16} /></Btn>
      <Btn on={() => c().setHorizontalRule().run()} title="Divider" testid="rte-hr"><Minus size={16} /></Btn>
      <Btn on={() => c().setTextAlign("left").run()} title="Align left" testid="rte-left"><AlignLeft size={16} /></Btn>
      <Btn on={() => c().setTextAlign("center").run()} active={st.center} title="Center" testid="rte-center"><AlignCenter size={16} /></Btn>
      <Btn on={() => c().setTextAlign("right").run()} active={st.right} title="Align right" testid="rte-right"><AlignRight size={16} /></Btn>
      <span className="w-px h-5 bg-white/10 mx-1" />
      <Btn on={setLink} active={st.link} title="Link" testid="rte-link"><Link2 size={16} /></Btn>
      <Btn on={() => fileRef.current?.click()} title="Insert image" testid="rte-image"><ImagePlus size={16} /></Btn>
      <Btn on={() => c().unsetAllMarks().clearNodes().run()} title="Clear formatting" testid="rte-clear"><Eraser size={16} /></Btn>
      <span className="ml-auto flex">
        <Btn on={() => c().undo().run()} title="Undo" testid="rte-undo"><Undo2 size={16} /></Btn>
        <Btn on={() => c().redo().run()} title="Redo" testid="rte-redo"><Redo2 size={16} /></Btn>
      </span>
      <input ref={fileRef} type="file" accept="image/*" hidden onChange={upload} />
    </div>
  );
}

export default function RichEditor({ value, onChange }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, link: { openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } } }),
      TextStyle, Color, FontSize, Image, TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: value || "",
    onUpdate: ({ editor: e }) => onChange(e.isEmpty ? "" : e.getHTML()),
    editorProps: { attributes: { class: "article-content min-h-[320px] px-4 py-3 focus:outline-none", "data-testid": "editor-content" } },
  });
  useEffect(() => {
    if (editor && value !== editor.getHTML() && !editor.isFocused) editor.commands.setContent(value || "", { emitUpdate: false });
  }, [value, editor]);
  if (!editor) return null;
  return (
    <div className="rounded-lg border border-white/10 bg-black/30 max-h-[560px] overflow-y-auto focus-within:border-[hsl(var(--primary))]" data-testid="rich-editor">
      <Toolbar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  );
}
